import React from 'react';
import './StupidSystemDesigns.css';

const friendRecommenderDesignCode = String.raw`addUser(user):
    if user.id already exists:
        return failure

    users[user.id] = user
    adjList[user.id] = empty set

    return success

connect(userId1, userId2):
    if either user does not exist:
        return failure

    if userId1 == userId2:
        return failure

    if userId2 is already a friend of userId1:
        return success

    recommendService.addConnection(userId1, userId2)
    return success

addConnection(id1, id2):
    adjList[id1].insert(id2)
    adjList[id2].insert(id1)

getRecommendations(userId):
    if userId does not exist:
        return failure

    recommendations = empty set
    friends = adjList[userId]

    for each friend in friends:
        for each candidate in adjList[friend]:
            if candidate == userId:
                continue

            if candidate is already in friends:
                continue

            recommendations.insert(candidate)

    return recommendations`;

function FriendRecommenderDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Friend Recommender Low Level Design</h1>
                            <p className="system-designs-subtitle">
                                Find new connections through a bidirectional friendship graph.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Users can register.</li>
                                <li>Users can connect with other users as friends.</li>
                                <li>Friendships are bidirectional.</li>
                                <li>Users can receive recommendations based on friends of friends.</li>
                                <li>Recommendations exclude the user and their existing friends.</li>
                                <li>Duplicate recommendations are avoided.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li>
                                    <strong>FriendsService:</strong> The entry point for user and friendship operations.
                                    It stores <code>users: Map&lt;UserId, User&gt;</code> and a <code>RecommendService</code>,
                                    and provides <code>addUser</code>, <code>connect</code>, and <code>getRecommendations</code>.
                                </li>
                                <li><strong>User:</strong> Contains a <code>userId</code> and optional metadata.</li>
                                <li>
                                    <strong>RecommendService:</strong> Maintains the friendship graph as
                                    {' '}<code>adjacency List: Map&lt;UserId, Set&lt;UserId&gt;&gt;</code> and exposes
                                    {' '}<code>addConnection</code> and <code>getRecommendations</code>.
                                    A set prevents duplicate friendships.
                                </li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{friendRecommenderDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Example</h2>
                            <p className="system-design-description">Suppose the friendship graph is:</p>
                            <pre className="design-code-block"><code>{`A -- B
A -- C
B -- D
C -- D
B -- E`}</code></pre>
                            <p className="system-design-description">
                                For user A, the direct friends are B and C. Their friends include A, D, and E.
                                Excluding A and existing friends B and C produces recommendations D and E.
                                D is returned only once, even though both B and C know D.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Complexity</h2>
                            <ul>
                                <li><strong>Add user:</strong> O(1) average.</li>
                                <li><strong>Connect users:</strong> O(1) average.</li>
                                <li>
                                    <strong>Get recommendations:</strong> O(F + D) average, where F is the number of direct friends
                                    and D is the total number of friends belonging to those direct friends.
                                </li>
                            </ul>
                            <p className="system-design-description">
                                <strong>Core design principle:</strong> FriendsService validates users and manages operations, while
                                RecommendService maintains the friendship graph and generates recommendations.
                                <br></br>
                                A good enhancement to this will be prioritise more common mutual friends' extended list in recommendations.
                            </p>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        A friend of a friend is a friend you haven’t awkwardly met yet. But really, what is a 'friend'?
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default FriendRecommenderDesign;
